import { useState, useEffect } from "react";
import { Table, Typography, Space, Input, Badge, Tooltip, Button, Popconfirm, message, Tag } from "antd";
import { SearchOutlined, PlusOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import axios from "axios";
import { useAuth0 } from "../../react-auth0-spa";
import { useAppContext } from "../../context";
import config from "../../api_config.json";
import featuresQuery from "./queries/featuresQuery";
import deleteFeatureMutation from "./queries/deleteFeatureMutation";
import sellerFeaturesByFeatureQuery from "./queries/sellerFeaturesByFeatureQuery";
import deleteSellerFeatureMutation from "./queries/deleteSellerFeatureMutation";
import ParseLogo from "../shared/images/parse.png";
import CreateFeatureModal from "./CreateFeatureModal";
import EditFeatureModal from "./EditFeatureModal";

const { Title } = Typography;
const { Search } = Input;

const PLAN_COLORS = {
  base: "default",
  verified: "blue",
  premium: "gold",
};

const MarketplaceFeatures = () => {
  const [loading, setLoading] = useState(true);
  const [features, setFeatures] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();
  const env = isProd ? "prod" : "dev";

  const fetchFeatures = async (page = 1, pageSize = 20, search = "") => {
    setLoading(true);
    try {
      const token = await getTokenSilently();
      const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;

      const where = {};
      if (search) {
        where.OR = [
          { name: { matchesRegex: search, options: "i" } },
          { label: { matchesRegex: search, options: "i" } },
        ];
      }

      const variables = {
        first: pageSize,
        skip: (page - 1) * pageSize,
        where: Object.keys(where).length > 0 ? where : undefined,
        order: ["name_ASC"],
      };

      const response = await axios.post(
        graphqlUrl,
        {
          query: featuresQuery,
          variables,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const edges = response.data?.marketplace_features?.edges || [];
      const count = response.data?.marketplace_features?.count || 0;

      setFeatures(edges.map((edge) => ({
        ...edge.node,
        plans: (edge.node.plans || []).map((p) => (typeof p === "string" ? p : p.value)),
      })));
      setPagination((prev) => ({ ...prev, current: page, pageSize, total: count }));
    } catch (error) {
      console.error("Error fetching marketplace features:", error);
      setFeatures([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeatures(pagination.current, pagination.pageSize, searchValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTableChange = (newPagination) => {
    fetchFeatures(newPagination.current, newPagination.pageSize, searchValue);
  };

  const handleSearch = (value) => {
    setSearchValue(value);
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchFeatures(1, pagination.pageSize, value);
  };

  const handleCreateFeature = () => {
    setCreateModalVisible(false);
    fetchFeatures(1, pagination.pageSize, searchValue);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleEditFeature = (feature) => {
    setSelectedFeature(feature);
    setEditModalVisible(true);
  };

  const handleUpdateFeature = () => {
    setEditModalVisible(false);
    setSelectedFeature(null);
    fetchFeatures(pagination.current, pagination.pageSize, searchValue);
  };

  const handleDelete = async (featureId) => {
    try {
      const token = await getTokenSilently();
      const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;
      const headers = { Authorization: `Bearer ${token}` };

      // Find and delete all seller_feature overrides pointing to this feature
      const sfResponse = await axios.post(
        graphqlUrl,
        {
          query: sellerFeaturesByFeatureQuery,
          variables: {
            where: {
              feature_pointer: {
                have: { objectId: { equalTo: featureId } },
              },
            },
          },
        },
        { headers }
      );

      const sellerFeatures = sfResponse.data?.marketplace_seller_features?.edges || [];
      await Promise.all(
        sellerFeatures.map((edge) =>
          axios.post(
            graphqlUrl,
            {
              query: deleteSellerFeatureMutation,
              variables: { id: edge.node.objectId },
            },
            { headers }
          )
        )
      );

      // Then delete the feature itself
      await axios.post(
        graphqlUrl,
        {
          query: deleteFeatureMutation,
          variables: { id: featureId },
        },
        { headers }
      );

      message.success("Feature eliminata con successo");
      fetchFeatures(pagination.current, pagination.pageSize, searchValue);
    } catch (error) {
      console.error("Error deleting feature:", error);
      message.error("Errore durante l'eliminazione della feature");
    }
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "objectId",
      key: "objectId",
      width: 100,
    },
    {
      title: "Nome",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
    },
    {
      title: "Label",
      dataIndex: "label",
      key: "label",
    },
    {
      title: "Piani",
      dataIndex: "plans",
      key: "plans",
      render: (plans) => (
        <Space size="small" wrap>
          {(plans || []).map((plan) => (
            <Tag color={PLAN_COLORS[plan] || "default"} key={plan}>
              {plan}
            </Tag>
          ))}
          {(!plans || plans.length === 0) && (
            <Tag>nessun piano</Tag>
          )}
        </Space>
      ),
    },
    {
      title: "Data Creazione",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => new Date(date).toLocaleDateString("it-IT"),
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    },
    {
      title: "Azioni",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEditFeature(record)}
          >
            Modifica
          </Button>
          <Popconfirm
            title="Sei sicuro di voler eliminare questa feature?"
            onConfirm={() => handleDelete(record.objectId)}
            okText="Si"
            cancelText="No"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              Elimina
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
    {
      title: "Links",
      key: "links",
      render: (_, record) => {
        const parseDashboardUrl = `${config.parseDashboard[isProd ? "prod" : "dev"]}/browser/marketplace_feature?filters=[{"field":"objectId","constraint":"eq","compareTo":"${record.objectId}"}]`;
        return (
          <Tooltip title="Visualizza in Parse Dashboard" placement="top">
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={parseDashboardUrl}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: "32px",
                height: "32px",
                borderRadius: "6px",
                backgroundColor: "transparent",
                transition: "all 0.2s ease",
              }}
            >
              <img
                src={ParseLogo}
                width={20}
                alt="Parse Dashboard"
                style={{ display: "block" }}
              />
            </a>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <div style={{ margin: "16px 16px" }}>
      <div style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Title level={3} style={{ margin: 0 }}>
              <Space>
                Features Marketplace
                <Badge overflowCount={5000} count={pagination.total} />
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setCreateModalVisible(true)}
                >
                  Crea Feature
                </Button>
              </Space>
            </Title>
            <Search
              placeholder="Cerca per nome o label"
              allowClear
              enterButton={<SearchOutlined />}
              size="large"
              onSearch={handleSearch}
              style={{ width: 400 }}
            />
          </div>
        </Space>
      </div>

      <Table
        dataSource={features}
        columns={columns}
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        rowKey="objectId"
        scroll={{ x: "max-content" }}
      />

      <CreateFeatureModal
        visible={createModalVisible}
        onCancel={() => setCreateModalVisible(false)}
        onCreate={handleCreateFeature}
      />

      <EditFeatureModal
        visible={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          setSelectedFeature(null);
        }}
        onUpdate={handleUpdateFeature}
        feature={selectedFeature}
      />
    </div>
  );
};

export default MarketplaceFeatures;
